import { ref, computed, watch } from "vue";
import { Org, Role } from "tf-api-client";
import api from "../api";
import { useActiveOrg } from "./use-active-org";

// `/org/:id` is for org admins only: for a standard user the API answers 401,
// which the session handler takes for an expired session and signs out.
const ORG_READER_ROLES: Role[] = [Role.OrgAdmin, Role.SuperAdmin];

export function useOrg() {
  const { org: activeOrg } = useActiveOrg();

  // The membership's real role, not the effective one: an admin previewing
  // the standard view keeps the API permissions of an admin.
  const canReadOrg = computed(
    () =>
      !!activeOrg.value && ORG_READER_ROLES.includes(activeOrg.value.role),
  );

  const org = ref<Org.OrganizationDetails | null>(null);
  const isLoading = ref(false);
  const error = ref<string | null>(null);

  const isManagerOrg = computed(
    () => org.value?.kind === Org.OrganizationKind.Manager,
  );

  const orgId = computed(() => activeOrg.value?.organizationId || null);

  const fetchOrg = async (id?: string | null) => {
    if (!canReadOrg.value) {
      org.value = null;
      return;
    }

    const useId = id ?? orgId.value;
    if (!useId) {
      error.value = "No organization id provided";
      org.value = null;
      return;
    }

    isLoading.value = true;
    error.value = null;
    try {
      const result = await api.org.getOrg(useId);
      org.value = result;
    } catch (err) {
      console.error("Failed to fetch org", err);
      error.value = "Failed to fetch organization details";
      org.value = null;
    } finally {
      isLoading.value = false;
    }
  };

  // the role changes with the membership: switching to an org where the user
  // is an admin has to fetch it
  watch(
    [orgId, canReadOrg],
    ([v]) => {
      if (v) fetchOrg(v);
      else {
        org.value = null;
      }
    },
    { immediate: true },
  );

  return {
    org,
    isLoading,
    error,
    fetchOrg,
    isManagerOrg,
  } as const;
}
